import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import type { AuthSession, UserEventAccess } from "@mvk/shared";
import { adminAuthApi } from "../../api/auth";
import { adminAuthStorage } from "../../api/authStorage";
import { isApiEnabled } from "../../api/config";

export type AdminRole = "super_admin" | "curator" | "exhibitor";

export interface AdminUser {
  id: string;
  name: string;
  email: string;
  initials: string;
  role: AdminRole;
  assignedEventId?: string;
  assignedCompanyId?: string;
  eventAccess: UserEventAccess[];
  isPlatformAdmin: boolean;
}

interface AdminAuthState {
  user: AdminUser | null;
  isLoading: boolean;
  loginWithPassword: (email: string, password: string) => Promise<void>;
  login: (user: AdminUser) => void;
  logout: () => void;
  refresh: () => Promise<void>;
}

const AdminAuthContext = createContext<AdminAuthState>({
  user: null,
  isLoading: true,
  loginWithPassword: async () => {},
  login: () => {},
  logout: () => {},
  refresh: async () => {},
});

const STORAGE_KEY = "mvk_admin_profile";

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("");
}

export function mapSessionToAdminUser(session: AuthSession): AdminUser {
  const { user, eventAccess } = session;

  if (user.isPlatformAdmin) {
    return {
      id: user.id,
      name: user.fullName,
      email: user.email ?? "",
      initials: initials(user.fullName),
      role: "super_admin",
      eventAccess,
      isPlatformAdmin: true,
    };
  }

  const organizers = eventAccess.filter((a) => a.role === "organizer" || a.role === "moderator");
  if (organizers.length) {
    // API already orders organizer access by soonest upcoming; skip any leftover stubs.
    const isStub = (eventId: string) =>
      /max-dm-support/i.test(eventId) || /organiser/i.test(eventId);
    const preferred = organizers.find((a) => !isStub(a.eventId)) ?? organizers[0]!;
    return {
      id: user.id,
      name: user.fullName,
      email: user.email ?? "",
      initials: initials(user.fullName),
      role: "curator",
      assignedEventId: preferred.eventId,
      eventAccess,
      isPlatformAdmin: false,
    };
  }

  const exhibitor = eventAccess.find((a) => a.role === "exhibitor");
  if (exhibitor) {
    return {
      id: user.id,
      name: user.fullName,
      email: user.email ?? "",
      initials: initials(user.fullName),
      role: "exhibitor",
      assignedEventId: exhibitor.eventId,
      assignedCompanyId: exhibitor.companyId,
      eventAccess,
      isPlatformAdmin: false,
    };
  }

  return {
    id: user.id,
    name: user.fullName,
    email: user.email ?? "",
    initials: initials(user.fullName),
    role: "curator",
    eventAccess,
    isPlatformAdmin: false,
  };
}

function persistUser(user: AdminUser | null) {
  if (user) localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
  else localStorage.removeItem(STORAGE_KEY);
}

export function AdminAuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const applyUser = useCallback((next: AdminUser | null) => {
    setUser(next);
    persistUser(next);
  }, []);

  const refresh = useCallback(async () => {
    if (!isApiEnabled() || !adminAuthStorage.getToken()) return;
    const payload = await adminAuthApi.me();
    applyUser(
      mapSessionToAdminUser({
        token: adminAuthStorage.getToken()!,
        user: payload.user,
        eventAccess: payload.eventAccess,
      }),
    );
  }, [applyUser]);

  useEffect(() => {
    (async () => {
      try {
        if (isApiEnabled() && adminAuthStorage.getToken()) {
          await refresh();
        } else {
          const stored = localStorage.getItem(STORAGE_KEY);
          if (stored) setUser(JSON.parse(stored));
        }
      } catch {
        adminAuthApi.logout();
        applyUser(null);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [applyUser, refresh]);

  const loginWithPassword = async (email: string, password: string) => {
    const session = await adminAuthApi.login(email, password);
    applyUser(mapSessionToAdminUser(session));
  };

  const login = (newUser: AdminUser) => {
    applyUser(newUser);
  };

  const logout = () => {
    adminAuthApi.logout();
    applyUser(null);
  };

  return (
    <AdminAuthContext.Provider
      value={{ user, isLoading, loginWithPassword, login, logout, refresh }}
    >
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  return useContext(AdminAuthContext);
}

export function canManageAllEvents(user: AdminUser | null) {
  return user?.isPlatformAdmin === true;
}

export function canManageEvent(user: AdminUser | null, eventId: string) {
  if (!user) return false;
  if (user.isPlatformAdmin) return true;
  if (user.role === "curator") {
    return user.eventAccess.some(
      (a) => a.eventId === eventId && (a.role === "organizer" || a.role === "moderator"),
    );
  }
  return false;
}

export function canManageCompany(user: AdminUser | null, companyId: string) {
  if (!user) return false;
  if (user.isPlatformAdmin) return true;
  if (user.role === "exhibitor") return user.assignedCompanyId === companyId;
  return false;
}

export function getRoleLabel(role: AdminRole): string {
  return { super_admin: "Ген. администратор", curator: "Куратор", exhibitor: "Экспонент" }[role];
}

export function getRoleBadgeColor(role: AdminRole): string {
  return {
    super_admin: "bg-indigo-100 text-indigo-700",
    curator: "bg-violet-100 text-violet-700",
    exhibitor: "bg-emerald-100 text-emerald-700",
  }[role];
}
